import LogoutIcon from '@mui/icons-material/LogoutRounded';
import {
  Box,
  Button,
  Typography,
  useTheme,
  Link as MuiLink,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import useIsAnonymousUser from 'hooks/useIsAnonymousUser';
import useProfile from 'hooks/useProfile';
import Cookies from 'js-cookie';
import { Trans, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import type { FC, JSX } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import contactInformation from 'constants/contact';
import documents from 'constants/documents';
import pageUrls from 'constants/pageUrls';

import ErrorText from 'components/ErrorText';
import ContractIcon from 'components/Icons/ContractIcon';
import Modal from 'components/Modal';
import CustomCheckbox from 'components/MuiCustom/CustomCheckbox';

import { useAppDispatch } from 'redux-store';
import acceptTermsThunk from 'redux-store/thunks/profile/acceptTermsThunk';
import getProfileThunk from 'redux-store/thunks/profile/getProfileThunk';
import logoutThunk from 'redux-store/thunks/profile/logoutThunk';

const TermsandConditionsModal: FC<DefaultPopupProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isAnonymous = useIsAnonymousUser();
  const dispatch = useAppDispatch();
  const router = useRouter();
  const profile = useProfile();

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);

  const acceptTermsandConditions = useCallback(() => {
    setAcceptTerms(prev => !prev);
  }, []);

  const handleAcceptTerms = useCallback(async () => {
    if (!acceptTerms) return;

    setLoading(true);
    setError(null);

    try {
      if (isAnonymous) {
        // Store terms in cookie for anonymous users
        Cookies.set('termsAccepted', 'true', {
          expires: 365,
          secure: true,
          sameSite: 'Lax',
        });

        onClose();
      } else {
        // Store terms for users with account in backend
        const response = await dispatch(acceptTermsThunk()).unwrap();

        if (response.success === false) {
          setError(t('common:something_went_wrong'));

          return;
        }
        dispatch(getProfileThunk({ userId: profile?.id }));

        onClose();
        router.reload();
      }
    } catch (err) {
      setError(t('common:something_went_wrong'));

      Sentry.captureException(err, {
        tags: { page: 'TermsandConditionsModal' },
      });
    } finally {
      setLoading(false);
    }
  }, [acceptTerms, dispatch, isAnonymous, onClose, profile, router, t]);

  const handleLogout = useCallback(async () => {
    await dispatch(logoutThunk());

    Sentry.setUser(null);

    await router.push(pageUrls.login, undefined, {
      locale: router.locale,
    });
  }, [dispatch, router]);

  const StyledLink = ({ email }: { email: string }): JSX.Element => (
    <a
      href={`mailto:${email}`}
      style={{ color: theme.vars.palette.primary.main }}
    >
      {email}
    </a>
  );

  return (
    <Modal open={open} hideCloseButton>
      <Box height="100%" display="flex" flexDirection="column">
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          mb={4}
          gap={0.5}
        >
          <ContractIcon sx={{ fontSize: 80, color: 'grey.400' }} />
        </Box>
        <Box>
          <ErrorText mt={1} error={error} />
          <Typography variant="h2" textAlign="center" mb={2}>
            {isAnonymous
              ? t('common:terms_conditions.anonymous_title')
              : t('common:terms_conditions.title')}
          </Typography>
          <Typography
            variant="body1"
            textAlign="center"
            mb={isAnonymous ? 1 : 2}
          >
            {isAnonymous
              ? t('common:terms_conditions.anonymous_description')
              : t('common:terms_conditions.description')}
          </Typography>

          <Typography variant="body1" textAlign="center" mb={2}>
            {isAnonymous ? (
              t('common:terms_conditions.anonymous_info')
            ) : (
              <Trans
                i18nKey="common:terms_conditions.info"
                t={t}
                components={{
                  a: <StyledLink email={contactInformation.email} />,
                }}
                values={{
                  email: contactInformation.email,
                }}
              />
            )}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" mt={2} gap={0.5}>
          <CustomCheckbox
            checked={acceptTerms}
            onChange={acceptTermsandConditions}
            data-testid="terms-conditions-checkbox"
          />
          <Typography variant="body1">
            {t('common:reset_password.terms_conditions_info') + ' '}
            {}
            <MuiLink
              href={documents.terms}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: theme.vars.palette.primary.main,
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              {t('common:reset_password.terms_conditions')}
            </MuiLink>
          </Typography>
        </Box>
        <Box mt={2} display="flex" flexDirection="column" gap={1}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleAcceptTerms}
            disabled={!acceptTerms || isLoading}
          >
            {t('common:continue')}
          </Button>
          <Button
            fullWidth
            variant="text"
            size="large"
            onClick={handleLogout}
            startIcon={<LogoutIcon sx={{ transform: 'rotate(180deg)' }} />}
          >
            {t('common:logout')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default TermsandConditionsModal;
